(function () {
  'use strict';

  const { iman, subEvents, ON_CHANGE_DEBOUNCE_TIMER, PUB_SUB_EVENTS, CartDrawer, ProductForms, QuantityInput, debounce, fetchConfig } = window.Universe;


  class CartNote {
    constructor(el) {
      if (!el) return;

      this.DOM = { el: el };
      this.setup();
    }

    destroy() {
      if (this.DOM) {
        this.DOM.el.removeEventListener('input', this.onInput);
        this.DOM = undefined;
      }
    }

    setup() {
      this.onInput = (event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(window.routes.cart_update_url, Object.assign({}, fetchConfig(), { body: body }));
      };

      this.DOM.el.addEventListener('input', debounce(this.onInput, ON_CHANGE_DEBOUNCE_TIMER));
    }
  }


  class CartDiscountCode {
    constructor(el, settings) {
      if (!el) return;

      this.DOM = { el: el };
      this.DOM.input = el.querySelector('.js-cart-discount-input');
      this.DOM.button = el.querySelector('.js-cart-discount-apply');
      this.DOM.message = el.querySelector('.js-cart-discount-message');
      this.settings = settings;
      this.isLoading = false;

      this.setup();
    }

    setup() {
      this.onKeyDown = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onApply();
        }
      };

      this.onApply = async () => {
        if (this.isLoading) return;

        const code = this.DOM.input?.value?.trim();
        if (!code) return;

        this.setLoading(true);
        this.clearMessage();

        try {
          const discountResponse = await fetch('/discount/' + encodeURIComponent(code), { method: 'GET' });

          if (!discountResponse.ok) {
            this.showMessage(this.settings.discountErrorText, 'error');
            return;
          }

          const cartResponse = await fetch(window.routes.cart_url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          });

          if (!cartResponse.ok) {
            this.showMessage(this.settings.discountErrorText, 'error');
            return;
          }

          const cartData = await cartResponse.json();
          const hasDiscount = cartData.cart_level_discount_applications?.length > 0 || cartData.total_discount > 0;

          if (hasDiscount) {
            this.showMessage(this.settings.discountAppliedText, 'success');
            this.DOM.input.value = '';

            subEvents.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'discount-code',
              cartData: cartData,
            });
          } else {
            this.showMessage(this.settings.discountErrorText, 'error');
          }
        } catch (error) {
          console.error('Discount code error:', error);
          this.showMessage(this.settings.discountErrorText, 'error');
        } finally {
          this.setLoading(false);
        }
      };

      this.setLoading = (loading) => {
        this.isLoading = loading;
        if (this.DOM.button) {
          this.DOM.button.classList.toggle('is-loading', loading);
          if (loading) {
            this.DOM.button.setAttribute('disabled', 'disabled');
          } else {
            this.DOM.button.removeAttribute('disabled');
          }
        }
      };

      this.showMessage = (text, type) => {
        if (!this.DOM.message) return;
        this.DOM.message.textContent = text;
        this.DOM.message.className = 'cart-drawer__discount-message js-cart-discount-message cart-drawer__discount-message--' + type;

        clearTimeout(this.messageTimeout);
        this.messageTimeout = setTimeout(() => {
          this.clearMessage();
        }, 5000);
      };

      this.clearMessage = () => {
        if (!this.DOM.message) return;
        this.DOM.message.textContent = '';
        this.DOM.message.className = 'cart-drawer__discount-message js-cart-discount-message';
      };

      if (this.DOM.button) {
        this.DOM.button.addEventListener('click', this.onApply);
      }
      if (this.DOM.input) {
        this.DOM.input.addEventListener('keydown', this.onKeyDown);
      }
    }

    destroy() {
      if (this.DOM) {
        clearTimeout(this.messageTimeout);
        this.DOM.button?.removeEventListener('click', this.onApply);
        this.DOM.input?.removeEventListener('keydown', this.onKeyDown);
        this.DOM = undefined;
      }
    }
  }


  class CartGiftWrap {
    constructor(el) {
      if (!el) return;

      this.DOM = { el: el };
      this.DOM.checkbox = el.querySelector('.js-cart-gift-wrap-check');
      if (!this.DOM.checkbox) return;

      this.DOM.message = el.querySelector('.js-cart-gift-wrap-message');
      this.variantId = this.DOM.checkbox.dataset.variantId;
      this.isLoading = false;

      this.setup();
    }

    setup() {
      this.onChange = () => {
        if (this.isLoading || !this.variantId) return;

        if (this.DOM.checkbox.checked) {
          this.addGiftWrap();
        } else {
          this.removeGiftWrap();
        }
      };

      this.setLoading = (loading) => {
        this.isLoading = loading;
        this.DOM.checkbox.disabled = loading;
      };

      this.publishUpdate = (cartData) => {
        subEvents.publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-wrap',
          cartData: cartData,
        });
      };

      this.updateGiftNote = (value) => {
        return fetch(window.routes.cart_update_url, Object.assign({}, fetchConfig(), {
          body: JSON.stringify({ attributes: { gift_note: value } }),
        }));
      };

      this.onMessageInput = debounce(() => {
        if (!this.DOM?.message) return;
        this.updateGiftNote(this.DOM.message.value);
      }, ON_CHANGE_DEBOUNCE_TIMER);

      this.addGiftWrap = async () => {
        this.setLoading(true);

        try {
          const response = await fetch(window.routes.cart_add_url, Object.assign({}, fetchConfig(), {
            body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
          }));

          if (!response.ok) {
            this.DOM.checkbox.checked = false;
            return;
          }

          const cartResponse = await fetch(window.routes.cart_url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          });
          const cartData = await cartResponse.json();
          this.publishUpdate(cartData);
        } catch (error) {
          console.error('Gift wrap add error:', error);
          this.DOM.checkbox.checked = false;
        } finally {
          this.setLoading(false);
        }
      };

      this.removeGiftWrap = async () => {
        this.setLoading(true);

        try {
          const response = await fetch(window.routes.cart_change_url, Object.assign({}, fetchConfig(), {
            body: JSON.stringify({ id: this.variantId, quantity: 0 }),
          }));

          if (!response.ok) {
            this.DOM.checkbox.checked = true;
            return;
          }

          if (this.DOM.message) {
            this.DOM.message.value = '';
          }
          // Non-critical: clear the gift note attribute without blocking or
          // reverting the removal if it fails.
          this.updateGiftNote('').catch(() => {});

          const cartData = await response.json();
          this.publishUpdate(cartData);
        } catch (error) {
          console.error('Gift wrap remove error:', error);
          this.DOM.checkbox.checked = true;
        } finally {
          this.setLoading(false);
        }
      };

      this.DOM.checkbox.addEventListener('change', this.onChange);
      if (this.DOM.message) {
        this.DOM.message.addEventListener('input', this.onMessageInput);
      }
    }

    destroy() {
      if (this.DOM) {
        this.DOM.checkbox?.removeEventListener('change', this.onChange);
        this.DOM.message?.removeEventListener('input', this.onMessageInput);
        this.DOM = undefined;
      }
    }
  }


  class CartItem {
    constructor(el) {
      if (!el) return;

      this.DOM = { el: el };
      this.DOM.quantityInput = this.DOM.el.querySelector('.js-cart-item-quantity');
      this.DOM.remover = this.DOM.el.querySelector('.js-cart-item-remove');
      this.DOM.error = this.DOM.el.querySelector('.js-cart-item-error');


      this.isLoading = false;
      this.cartErrorUnsubscriber = undefined;
      this.errorTimeout = undefined;
      this.abortController = undefined;

      this.setup();
    }

    setup() {
      this.showError = (message) => {
        if (!this.DOM?.error) return;

        clearTimeout(this.errorTimeout);

        this.DOM.error.textContent = message;
        this.DOM.error.classList.remove('is-hidden');
        this.DOM.error.classList.add('is-visible');

        this.errorTimeout = setTimeout(() => {
          if (!this.DOM?.error) return;
          this.DOM.error.classList.remove('is-visible');
          this.DOM.error.classList.add('is-hidden');
        }, 5000);
      };

      this.clearError = () => {
        if (!this.DOM?.error) return;
        clearTimeout(this.errorTimeout);
        this.DOM.error.classList.remove('is-visible');
        this.DOM.error.classList.add('is-hidden');
      };

      this.setIsLoading = (loading) => {
        this.isLoading = loading;
        document.body.classList.toggle('is-cart-loading', loading);
        if (this.DOM.quantityInput) {
          this.DOM.quantityInput.classList.toggle('is-loading', loading);
        }
        if (this.DOM.remover) {
          this.DOM.remover.classList.toggle('is-loading', loading);
        }
      };

      this.onRemove = (event) => {
        event.preventDefault();

        if (this.isLoading) return;

        this.updateQuantity({
          key: event.currentTarget.dataset.key,
          quantity: 0,
          variantId: event.currentTarget.dataset.variantId,
        });
      };

      this.onCartError = (event) => {
        if (
          event.source === 'cart-items' &&
          event.variantId === this.DOM.quantityInput?.querySelector('.js-quantity-input')?.dataset.quantityVariantId
        ) {
          this.showError(event.message);
        }
      };

      this.validateItemQuantity = (event) => {
        if (!this.DOM) return;

        if (this.abortController) {
          this.abortController.abort();
          this.abortController = undefined;
        }

        const inputValue = parseInt(event.target.value);
        const key = event.target.dataset.key;
        const quantityVariantId = event.target.dataset.quantityVariantId;
        let message = '';

        if (inputValue < parseInt(event.target.dataset.min)) {
          message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
        } else if (inputValue > parseInt(event.target.max)) {
          message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
        } else if (inputValue % parseInt(event.target.step) !== 0) {
          message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
        }

        if (message) {
          this.showError(message);
          return;
        }

        this.debouncedUpdate({
          key: key,
          quantity: inputValue,
          variantId: quantityVariantId,
        });
      };

      this.updateQuantity = async (ref) => {
        const { key, quantity, variantId } = ref;

        if (!this.DOM) return;

        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        this.setIsLoading(true);
        this.clearError();

        if (!key) {
          console.error('CartItem.updateQuantity: key is missing', { key: key, quantity: quantity, variantId: variantId });
          this.showError(window.cartStrings?.cart_error || 'Cart update failed');
          this.setIsLoading(false);
          return;
        }

        if (!routes?.cart_change_url) {
          console.error('CartItem.updateQuantity: routes.cart_change_url is missing');
          this.showError(window.cartStrings?.cart_error || 'Cart update failed');
          this.setIsLoading(false);
          return;
        }

        const body = JSON.stringify({
          id: key,
          quantity: quantity,
        });

        try {
          const response = await fetch(routes.cart_change_url, Object.assign(
            {},
            fetchConfig(),
            { body: body, signal: this.abortController.signal }
          ));
          const state = await response.text();
          const parsedState = JSON.parse(state);

          if (parsedState.errors || parsedState.status) {
            this.quantityInput.DOM.input.setCustomValidity(parsedState.errors);
            this.quantityInput.DOM.input.reportValidity();
            this.quantityInput.DOM.input.value = this.quantityInput.DOM.input.getAttribute('value');

            let errorMessage = window.cartStrings.insufficientStockError;

            if (parsedState.errors && typeof parsedState.errors === 'object') {
              const errorValues = Object.values(parsedState.errors);
              if (errorValues.length > 0 && errorValues[0] && errorValues[0].length > 1) {
                errorMessage = errorValues[0];
              }
            } else if (parsedState.description && parsedState.description.length > 1) {
              errorMessage = parsedState.description;
            } else if (parsedState.message && parsedState.message.length > 1) {
              errorMessage = parsedState.message;
            }

            this.showError(errorMessage);
            return;
          }

          subEvents.publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: parsedState,
            variantId: variantId,
          });
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Failed to update cart:', error);
          this.showError(window.cartStrings?.cart_error || 'Cart update failed');
        } finally {
          this.setIsLoading(false);
        }
      };

      this.debouncedUpdate = debounce(this.updateQuantity, ON_CHANGE_DEBOUNCE_TIMER);
      this.DOM.el.addEventListener('change', this.validateItemQuantity);

      if (this.DOM.quantityInput) {
        this.quantityInput = new QuantityInput(this.DOM.quantityInput);
      }

      if (this.DOM.remover) {

        this.DOM.remover.addEventListener('click', this.onRemove);
      }

      this.cartErrorUnsubscriber = subEvents.subscribe(PUB_SUB_EVENTS.cartError, this.onCartError);
    }

    destroy() {
      if (this.abortController) {
        this.abortController.abort();
      }
      clearTimeout(this.errorTimeout);

      if (this.DOM?.el) {
        this.DOM.el.removeEventListener('change', this.validateItemQuantity);
      }

      if (this.quantityInput) {
        this.quantityInput.destroy();
      }

      if (this.DOM?.remover) {
        this.DOM.remover.removeEventListener('click', this.onRemove);
      }

      if (this.cartErrorUnsubscriber) {
        this.cartErrorUnsubscriber();
      }

      this.quantityInput = undefined;
      this.cartErrorUnsubscriber = undefined;
      this.errorTimeout = undefined;
      this.DOM = undefined;
    }
  }


  class Cart {
    constructor() {
      this.init();
    }

    init() {
      this.DOM = {};
      this.DOM.drawerWrap = document.querySelector('.js-cart-drawer-wrap');

      this.onCartUpdate = (data) => {
        this.updateDrawerTemplate(data?.source);
        this.update();
      };

      this.cartUpdateUnsubscriber = subEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      this.cartItems = [];
      this.notes = [];

      if (!this.DOM.drawerWrap) return;

      this.DOM.drawerNotesWrap = this.DOM.drawerWrap.querySelector('.js-cart-drawer-notes-wrap');
      this.DOM.drawerConversionWrap = this.DOM.drawerWrap.querySelector('.js-cart-drawer-conversion-wrap');
      this.DOM.drawerConversionIcon = this.DOM.drawerWrap.querySelector('.js-cart-drawer-conversion-icon svg');
      this.DOM.drawerViewCart = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-cart');
      this.DOM.drawerViewCartButton = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-cart-button');
      this.DOM.drawerViewCheckout = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-checkout');
      this.DOM.drawerViewCheckoutButton = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-checkout-button');
      this.DOM.drawerMobileCrossSell = this.DOM.drawerWrap.querySelector('.js-cart-drawer-mobile-cross-sell');
      this.DOM.drawerMobileCrossSellContent = this.DOM.drawerWrap.querySelector(
        '.js-cart-drawer-mobile-cross-sell-content',
      );
      this.DOM.totalCounters = document.querySelectorAll('.js-cart-total-counter');
      this.DOM.cartItems = this.DOM.drawerWrap.querySelectorAll('.js-cart-item');
      this.DOM.notesTextarea = this.DOM.drawerWrap.querySelectorAll('.js-cart-note');

      this.drawerSectionSettings = window.cartDrawerSectionSettings;
      this.cartItems = [];
      this.notes = [];
      this.discountCode = null;
      this.giftWrap = null;

      if (this.DOM.cartItems.length) {
        this.cartItems = Array.from(this.DOM.cartItems).map((el) => new CartItem(el));
      }

      if (this.DOM.notesTextarea.length) {
        this.notes = Array.from(this.DOM.notesTextarea).map((el) => new CartNote(el));
      }

      this.initDiscountCode();
      this.initGiftWrap();

      this.cartDrawer = new CartDrawer();
    }

    update() {
      fetch(window.routes.cart_url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          const itemCount = parsedState.item_count;

          const allTotalCounters = document.querySelectorAll('.js-cart-total-counter');

          if (allTotalCounters.length) {
            Array.from(allTotalCounters).forEach((counter) => {
              counter.textContent = itemCount;
            });
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }

    initDiscountCode() {
      if (!this.DOM?.drawerWrap) return;

      if (this.discountCode) {
        this.discountCode.destroy();
      }
      this.discountCode = null;

      const discountWrap = this.DOM.drawerWrap.querySelector('.cart-drawer__discount-code-wrap');
      if (discountWrap) {
        this.discountCode = new CartDiscountCode(discountWrap, this.drawerSectionSettings);
      }
    }

    initGiftWrap() {
      if (!this.DOM?.drawerWrap) return;

      if (this.giftWrap) {
        this.giftWrap.destroy();
      }
      this.giftWrap = null;

      const giftWrapWrap = this.DOM.drawerWrap.querySelector('.js-cart-drawer-gift-wrap-wrap');
      if (giftWrapWrap) {
        this.giftWrap = new CartGiftWrap(giftWrapWrap);
      }
    }

    syncGiftWrapChecked(html) {
      if (!this.DOM?.drawerWrap || !html) return;

      const liveCheck = this.DOM.drawerWrap.querySelector('.js-cart-drawer-gift-wrap-wrap .js-cart-gift-wrap-check');
      const newCheck = html.querySelector('.js-cart-drawer-gift-wrap-wrap .js-cart-gift-wrap-check');
      if (liveCheck && newCheck) {
        liveCheck.checked = newCheck.checked;
      }
    }

    updateDrawerBody() {
      if (!this.DOM?.drawerWrap || !this.drawerSectionSettings) return;

      if (
        this.DOM.drawerNotesWrap &&
        this.drawerSectionSettings.showNotes &&
        !this.DOM.drawerWrap.classList.contains('is-empty')
      ) {
        const textarea = this.DOM.drawerWrap.querySelector('.js-cart-note');
        if (textarea && this.drawerSectionSettings.noteValue !== undefined) {
          textarea.value = this.drawerSectionSettings.noteValue;
        }
      }

      if (
        this.DOM.drawerMobileCrossSell &&
        this.drawerSectionSettings.enableCrossSell &&
        !this.DOM.drawerWrap.classList.contains('is-empty')
      ) {
        this.DOM.drawerMobileCrossSell = this.DOM.drawerWrap.querySelector('.js-cart-drawer-mobile-cross-sell');

        const hasProducts = this.DOM.drawerMobileCrossSell.querySelector('.quick-add-product-card');
        if (!hasProducts) {
          this.DOM.drawerMobileCrossSell.innerHTML = this.DOM.drawerMobileCrossSellContent.innerHTML;
        }
      }
    }

    updateDrawerFooter() {
      if (!this.DOM?.drawerWrap || !this.drawerSectionSettings) return;

      this.DOM.drawerViewCart = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-cart');
      this.DOM.drawerViewCheckout = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-checkout');
      this.DOM.drawerViewCheckoutButton = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-checkout-button');
      this.DOM.drawerViewCartButton = this.DOM.drawerWrap.querySelector('.js-cart-drawer-view-cart-button');

      if (!this.DOM.drawerWrap.classList.contains('is-empty')) {
        /* NOTE: innerHTML used intentionally for same-origin Shopify section content */
        if (this.DOM.drawerViewCart && this.DOM.drawerViewCartButton && this.drawerSectionSettings.showViewCartButton) {
          this.DOM.drawerViewCart.classList.remove('hidden');
          this.DOM.drawerViewCart.innerHTML = this.DOM.drawerViewCartButton.innerHTML;
        } else if (!this.drawerSectionSettings.showViewCartButton && this.DOM.drawerViewCart) {
          this.DOM.drawerViewCart.classList.add('hidden');
        }

        if (
          this.DOM.drawerViewCheckout &&
          this.DOM.drawerViewCheckoutButton &&
          this.drawerSectionSettings.showCheckoutButton
        ) {
          this.DOM.drawerViewCheckout.classList.remove('hidden');
          this.DOM.drawerViewCheckout.innerHTML = this.DOM.drawerViewCheckoutButton.innerHTML;

          this.setupCheckoutButtonState();
        } else if (!this.drawerSectionSettings.showCheckoutButton && this.DOM.drawerViewCheckout) {
          this.DOM.drawerViewCheckout.classList.add('hidden');
        }
      }
    }

    setupCheckoutButtonState() {
      if (!this.DOM?.drawerWrap) return;

      const termsCheckbox = this.DOM.drawerWrap.querySelector('.js-cart-drawer-terms');
      const checkoutButtons = this.DOM.drawerWrap.querySelectorAll('.cart-drawer__checkout--final');

      const updateButtonState = () => {
        const isValid = !termsCheckbox || termsCheckbox.checked;
        checkoutButtons.forEach((button) => {
          if (isValid) {
            button.removeAttribute('disabled');
          } else {
            button.setAttribute('disabled', 'disabled');
          }
        });
      };

      updateButtonState();

      if (termsCheckbox) {
        termsCheckbox.removeEventListener('change', updateButtonState);
        termsCheckbox.addEventListener('change', updateButtonState);
      }
    }

    waitForDrawerAnimation() {
      if (!this.cartDrawer?.isAnimating) return Promise.resolve();

      return new Promise((resolve) => {
        const checkAnimation = () => {
          if (!this.cartDrawer?.isAnimating) {
            resolve();
          } else {
            requestAnimationFrame(checkAnimation);
          }
        };
        checkAnimation();
      });
    }

    async updateDrawerTemplate(source) {
      if (!this.DOM?.drawerWrap) return;

      await this.waitForDrawerAnimation();

      try {
        const drawerShopifySection = this.DOM.drawerWrap.closest('.shopify-section');
        const drawerSectionId = drawerShopifySection ? drawerShopifySection.id.replace(/^shopify-section-/, '') : 'u-drawer-cart';
        const response = await fetch(window.Shopify.routes.root + '?section_id=' + encodeURIComponent(drawerSectionId));
        const responseText = await response.text();

        requestAnimationFrame(() => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const gotItems = html.querySelector('.js-cart-drawer-items-form');
          this.DOM.drawerWrap.classList.toggle('is-empty', !gotItems);

          const currentCrossSell = this.DOM.drawerWrap.querySelector('.js-cart-drawer-mobile-cross-sell');
          const crossSellContent = currentCrossSell ? currentCrossSell.innerHTML : null;

          const selectors = [
            '.js-cart-drawer-total-price',
            '.js-cart-drawer-total',
            '.js-cart-drawer-item-contents',
            '.js-cart-drawer-footer',
          ];
          for (let s = 0; s < selectors.length; s++) {
            const selector = selectors[s];
            const targetElement = this.DOM.drawerWrap.querySelector(selector);
            const sourceElement = html.querySelector(selector);

            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          if (crossSellContent) {
            const newCrossSell = this.DOM.drawerWrap.querySelector('.js-cart-drawer-mobile-cross-sell');
            const newHasProducts = newCrossSell && newCrossSell.querySelector('.quick-add-product-card');
            if (newCrossSell && !newHasProducts) {
              newCrossSell.innerHTML = crossSellContent;
            }
          }

          if (this.cartDrawer?.DOM) {
            this.cartDrawer.DOM.terms = this.DOM.drawerWrap.querySelector('.js-cart-drawer-terms');
            this.cartDrawer.DOM.atc = this.DOM.drawerWrap.querySelectorAll('.js-cart-drawer-atc');
          }

          if (!this.drawerSectionSettings) {
            this.drawerSectionSettings = window.cartDrawerSectionSettings;
          }

          this.syncGiftWrapChecked(html);
          this.updateDrawerBody();
          this.updateDrawerFooter();
          this.refresh();

          document.dispatchEvent(new CustomEvent('cart-drawer:template-updated', {
            detail: { isEmpty: !gotItems, source: source },
          }));
        });
      } catch (error) {
        console.error('Failed to update cart drawer template:', error);
      }
    }

    refreshDiscountCode() {
      this.initDiscountCode();
    }

    refreshGiftWrap() {
      this.initGiftWrap();
    }

    refreshNotes() {
      if (!this.DOM?.drawerWrap) return;

      this.notes.forEach((note) => { note.destroy(); });
      this.notes = [];

      this.DOM.notesTextarea = this.DOM.drawerWrap.querySelectorAll('.js-cart-note');
      if (this.DOM.notesTextarea.length) {
        this.notes = Array.from(this.DOM.notesTextarea).map((el) => new CartNote(el));
      }
    }

    refreshCartItems() {
      if (!this.DOM?.drawerWrap) return;

      this.cartItems.forEach((item) => { item.destroy(); });
      this.cartItems = [];

      this.DOM.cartItems = this.DOM.drawerWrap.querySelectorAll('.js-cart-item');
      if (this.DOM.cartItems.length) {
        this.cartItems = Array.from(this.DOM.cartItems).map((el) => new CartItem(el));
      }
    }

    refreshProductForms() {
      const productForms = iman.get('productForms');
      if (productForms) {
        productForms.destroy();
        iman.remove('productForms');
        iman.add('productForms', new ProductForms());
      }
    }

    refresh() {
      this.cartDrawer?.updateTerms();
      this.refreshNotes();
      this.refreshDiscountCode();
      this.refreshGiftWrap();
      this.refreshProductForms();
      this.refreshCartItems();
      this.cartDrawer?.refresh();
    }

    reinit() {
      this.cartDrawer?.reinit();
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      if (sectionEl.querySelector('.js-cart-drawer-wrap')) {
        this.cartDrawer?.reinit();
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        this.cartItems.forEach((cartItem) => { cartItem.destroy(); });
        this.notes.forEach((note) => { note.destroy(); });

        if (this.discountCode) {
          this.discountCode.destroy();
          this.discountCode = null;
        }

        if (this.giftWrap) {
          this.giftWrap.destroy();
          this.giftWrap = null;
        }

        this.cartDrawer?.destroy();
        this.notes = [];
        this.DOM = undefined;
      }
    }
  }

  window.Universe = window.Universe || {};
  window.Universe.Cart = Cart;
  window.Universe.CartItem = CartItem;
  window.Universe.CartNote = CartNote;
  window.Universe.CartDiscountCode = CartDiscountCode;
  window.Universe.CartGiftWrap = CartGiftWrap;
})();
